import { useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { clearAuthToken } from '@/App';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  Settings,
  Briefcase,
  Images,
  MessageSquare,
  Users,
  Mail,
  LogOut,
  Menu,
  X,
  Home,
  BookOpen,
} from 'lucide-react';

// Admin sections
import AdminSiteContent from '@/pages/admin/sections/AdminSiteContent';
import AdminServices from '@/pages/admin/sections/AdminServices';
import AdminPortfolio from '@/pages/admin/sections/AdminPortfolio';
import AdminTestimonials from '@/pages/admin/sections/AdminTestimonials';
import AdminVendors from '@/pages/admin/sections/AdminVendors';
import AdminInquiries from '@/pages/admin/sections/AdminInquiries';
import AdminClients from '@/pages/admin/sections/AdminClients';
import AdminProjects from '@/pages/admin/sections/AdminProjects';
import AdminTutorial from '@/pages/admin/sections/AdminTutorial';

const AdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/site-content', label: 'Site Content', icon: Settings },
    { path: '/admin/services', label: 'Services', icon: Briefcase },
    { path: '/admin/portfolio', label: 'Portfolio', icon: Images },
    { path: '/admin/testimonials', label: 'Testimonials', icon: MessageSquare },
    { path: '/admin/vendors', label: 'Vendors', icon: Users },
    { path: '/admin/inquiries', label: 'Inquiries', icon: Mail },
    { path: '/admin/clients', label: 'Clients', icon: Users },
    { path: '/admin/projects', label: 'Projects', icon: Mail },
    { path: '/admin/tutorial', label: 'Tutorial', icon: BookOpen },
  ];

  const handleLogout = () => {
    clearAuthToken();
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F5F5F0] flex">
      {/* Sidebar */}
      <aside
        className={`bg-[#4A5D23] fixed lg:static inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/logo.png"
                alt="Eden's Dawn"
                className="h-10 brightness-0 invert"
              />
            </Link>
            <p className="text-sm text-white/50 mt-1">Admin Panel</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                onClick={() => setIsSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all ${
                  isActive(item.path)
                    ? 'bg-[#D4AF37] text-white'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <item.icon size={18} />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-white/10">
            <Button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              variant="ghost"
              className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
            >
              <LogOut size={18} className="mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="bg-white shadow-soft sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              data-testid="admin-sidebar-toggle"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            <div className="flex items-center gap-4">
              <Link to="/" target="_blank">
                <Button variant="outline" size="sm" className="text-[#4A5D23] border-[#4A5D23]">
                  <Home size={16} className="mr-2" /> View Site
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6">
          <Routes>
            <Route path="dashboard" element={<DashboardHome />} />
            <Route path="site-content" element={<AdminSiteContent />} />
            <Route path="services" element={<AdminServices />} />
            <Route path="portfolio" element={<AdminPortfolio />} />
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="vendors" element={<AdminVendors />} />
            <Route path="inquiries" element={<AdminInquiries />} />
            <Route path="clients" element={<AdminClients />} />
            <Route path="projects" element={<AdminProjects />} />
            <Route path="tutorial" element={<AdminTutorial />} />
            <Route path="*" element={<DashboardHome />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

// Dashboard Home Component
const DashboardHome = () => {
  return (
    <div data-testid="admin-dashboard-home">
      <h1 className="font-heading text-3xl text-[#2C2C2C] mb-8">Welcome Back!</h1>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[
          { label: 'Services', value: '5', icon: Briefcase, color: 'bg-[#4A5D23]' },
          { label: 'Portfolio', value: '6', icon: Images, color: 'bg-[#D4AF37]' },
          { label: 'Testimonials', value: '3', icon: MessageSquare, color: 'bg-[#C06C55]' },
          { label: 'Inquiries', value: 'New', icon: Mail, color: 'bg-[#6B7F3A]' },
        ].map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-2xl shadow-soft p-6 flex items-center gap-4"
          >
            <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
              <stat.icon className="text-white" size={24} />
            </div>
            <div>
              <p className="text-sm text-[#6B7280]">{stat.label}</p>
              <p className="font-heading text-2xl text-[#2C2C2C]">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-soft p-8">
        <h2 className="font-heading text-xl text-[#2C2C2C] mb-6">Quick Actions</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/admin/services">
            <Button variant="outline" className="w-full justify-start border-[#E8DFD0] hover:bg-[#F5F5F0]">
              <Briefcase size={18} className="mr-2 text-[#4A5D23]" /> Manage Services
            </Button>
          </Link>
          <Link to="/admin/portfolio">
            <Button variant="outline" className="w-full justify-start border-[#E8DFD0] hover:bg-[#F5F5F0]">
              <Images size={18} className="mr-2 text-[#D4AF37]" /> Add Portfolio
            </Button>
          </Link>
          <Link to="/admin/inquiries">
            <Button variant="outline" className="w-full justify-start border-[#E8DFD0] hover:bg-[#F5F5F0]">
              <Mail size={18} className="mr-2 text-[#C06C55]" /> View Inquiries
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
